/**
 * Componente de Rota Protegida
 * Usa o hook useAuth genérico - independente de provedor
 * Inclui verificação adicional de token para garantir autenticação válida
 */
import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, updateToken, isAuthenticated as checkAuth } from '@/features/auth';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation("shared");
  const [isVerifying, setIsVerifying] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const verifyAuth = async () => {
      if (isLoading || isAuthenticated || isVerifying) {
        return;
      }

      setIsVerifying(true);

      try {
        /** Tenta renovar token uma vez */
        await updateToken(5);

        /** Re-verifica estado de autenticação */
        if (checkAuth()) {
          setShouldRedirect(false);
        } else {
          setShouldRedirect(true);
        }
      } catch {
        setShouldRedirect(true);
      } finally {
        setIsVerifying(false);
      }
    };

    /** Apenas faz verificação adicional se não autenticado */
    if (!isLoading && !isAuthenticated && !isVerifying) {
      verifyAuth();
    }
  }, [isAuthenticated, isLoading, isVerifying]);

  /** Estado de carregamento */
  if (isLoading || isVerifying) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex items-center space-x-2">
          <span className="h-8 w-8 animate-spin rounded-full border-4 border-visagio-mint border-t-transparent"></span>
          <span className="text-lg">{t("auth.loading")}</span>
        </div>
      </div>
    );
  }

  /** Redirecionar para login se não autenticado */
  if (!isAuthenticated && shouldRedirect) {
    return <Navigate to="/login" replace />;
  }

  /** Autenticado - renderizar filhos */
  if (isAuthenticated || checkAuth()) {
    return <>{children}</>;
  }

  /** Ainda verificando */
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex items-center space-x-2">
        <span className="h-8 w-8 animate-spin rounded-full border-4 border-visagio-mint border-t-transparent"></span>
        <span className="text-lg">{t("auth.verifyingAuth")}</span>
      </div>
    </div>
  );
}
